import { InputAdornment } from '@mui/material';
import TextField, { OutlinedTextFieldProps } from '@mui/material/TextField';
import { FC, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { getCurrencySymbol } from '../../utils';

type Props = Pick<
  OutlinedTextFieldProps,
  | 'name'
  | 'id'
  | 'fullWidth'
  | 'size'
  | 'placeholder'
  | 'disabled'
  | 'error'
  | 'helperText'
  | 'slotProps'
  | 'onFocus'
> & {
  value?: number | null;
  min?: number;
  max?: number;
  decimals?: number;
  maxDecimals?: number;
  currency?: string;
  currencyStartAdornment?: boolean;
  onChange?: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  hideZeroValue?: boolean;
  showPercentSuffix?: boolean;
  suffix?: string;
  clearZeroValueOnFocus?: boolean;
  fixedDecimalScale?: boolean;
  textAlign?: 'left' | 'right';
};

const getDecimalPlaces = (val: number): number => {
  const str = String(val);
  const dotIndex = str.indexOf('.');
  return dotIndex === -1 ? 0 : str.length - dotIndex - 1;
};

const formatWithMinDecimals = (
  val: number,
  minDecimals: number,
  maxDecimals: number,
): string => {
  const places = Math.min(
    maxDecimals,
    Math.max(minDecimals, getDecimalPlaces(val)),
  );
  return val.toFixed(places);
};

const NumberInput: FC<Props> = ({
  decimals = 0,
  maxDecimals,
  size = 'small',
  value,
  onChange,
  onBlur,
  currency,
  hideZeroValue = false,
  currencyStartAdornment = false,
  showPercentSuffix = false,
  suffix,
  clearZeroValueOnFocus = false,
  fixedDecimalScale = false,
  textAlign,
  min,
  max,
  ...rest
}) => {
  const formatValue = (
    val: number | null | undefined,
  ): string | number | null | undefined =>
    maxDecimals !== undefined && val != null
      ? formatWithMinDecimals(val, decimals, maxDecimals)
      : val;

  const [displayValue, setDisplayValue] = useState<
    string | number | null | undefined
  >(formatValue(value));
  const hasChangedRef = useRef(false);

  useEffect(() => {
    setDisplayValue(formatValue(value));
  }, [value, decimals, maxDecimals]); // eslint-disable-line react-hooks/exhaustive-deps

  const getValue = (value: string) => {
    const rawValue =
      currency && !currencyStartAdornment
        ? value.replace(getCurrencySymbol(currency), '')
        : value;

    const activeSuffix = suffix ?? (showPercentSuffix ? '%' : '');
    return Number(rawValue.replace(activeSuffix, '').replace(/,/g, ''));
  };

  return (
    <NumericFormat
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      value={hideZeroValue ? displayValue || '' : displayValue}
      customInput={TextField}
      prefix={
        currency && !currencyStartAdornment
          ? getCurrencySymbol(currency)
          : undefined
      }
      type="text"
      fixedDecimalScale={
        maxDecimals !== undefined ? false : fixedDecimalScale || !!currency
      }
      decimalScale={maxDecimals ?? decimals}
      allowNegative={min === undefined || min < 0}
      allowLeadingZeros={false}
      isAllowed={({ floatValue }) => {
        if (floatValue === undefined) return true;
        if (min !== undefined && floatValue < min) return false;
        if (max !== undefined && floatValue > max) return false;
        return true;
      }}
      suffix={suffix ?? (showPercentSuffix ? '%' : undefined)}
      onFocus={() => {
        if (clearZeroValueOnFocus && !value) {
          hasChangedRef.current = false;
          setDisplayValue('');
        }
      }}
      onChange={(event) => {
        hasChangedRef.current = true;
        onChange?.(getValue(event.target.value));
      }}
      onBlur={(event) => {
        if (clearZeroValueOnFocus && !hasChangedRef.current) {
          setDisplayValue(formatValue(value));
        } else {
          onBlur?.(getValue(event.target.value));
        }

        hasChangedRef.current = false;
      }}
      thousandSeparator
      size={size}
      variant="outlined"
      {...rest}
      slotProps={{
        ...(rest.slotProps && {
          ...rest.slotProps,
        }),
        htmlInput: {
          ...(typeof rest.slotProps?.htmlInput === 'object'
            ? rest.slotProps.htmlInput
            : {}),
          ...(textAlign && { style: { textAlign } }),
        },
        input: {
          ...(typeof rest.slotProps?.input === 'object'
            ? rest.slotProps.input
            : {}),
          startAdornment:
            currencyStartAdornment && currency ? (
              <InputAdornment position="start">
                {getCurrencySymbol(currency)}
              </InputAdornment>
            ) : typeof rest.slotProps?.input === 'object' ? (
              rest.slotProps.input?.startAdornment
            ) : undefined,
          endAdornment:
            typeof rest.slotProps?.input === 'object'
              ? rest.slotProps.input?.endAdornment
              : undefined,
        },
      }}
    />
  );
};

export default NumberInput;
